import { createClient } from '@/lib/supabase/client';

export const REVIEW_CONTEXTS = [
  { value: 'webinar', label: 'Free masterclass' },
  { value: 'course1', label: 'Course 1' },
  { value: 'membership', label: 'Membership' },
  { value: 'mentorship', label: 'Mentorship sessions' },
  { value: 'other', label: 'Other' },
] as const;

export type ReviewContext = (typeof REVIEW_CONTEXTS)[number]['value'];

export function contextLabel(value: string | null | undefined): string {
  const found = REVIEW_CONTEXTS.find((c) => c.value === value);
  return found ? found.label : value ? String(value) : '';
}

export function isReviewContext(value: string | null | undefined): value is ReviewContext {
  return REVIEW_CONTEXTS.some((c) => c.value === value);
}

export interface SubmitReviewInput {
  fullName: string;
  email: string;
  context: string;
  rating: number;
  review: string;
  displayName: string;
  businessName: string;
  consentToPublish: boolean;
  source: string;
}

export async function submitPublicReview(input: SubmitReviewInput): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.rpc('submit_public_review', {
    p_full_name: input.fullName,
    p_email: input.email,
    p_context: input.context,
    p_rating: input.rating,
    p_review: input.review,
    p_display_name: input.displayName || null,
    p_business_name: input.businessName || null,
    p_consent_to_publish: input.consentToPublish,
    p_source: input.source,
  });
  if (error) throw new Error(error.message || 'Your review could not be sent.');
}

export interface HomepageReview {
  displayName: string;
  businessName: string;
  rating: number;
  quote: string;
  context: string;
  verifiedAs: string;
  publishedAt: string | null;
}

export interface HomepageReviewsData {
  reviews: HomepageReview[];
  total: number;
  average: number;
}

export async function fetchHomepageReviews(limit = 9): Promise<HomepageReviewsData> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('homepage_reviews', { p_limit: limit });
  if (error) throw new Error(error.message);
  const rows: any[] = Array.isArray(data) ? data : [];
  const reviews: HomepageReview[] = rows
    .filter((r) => r && r.quote)
    .map((r) => ({
      displayName: r.display_name || 'Care Business School learner',
      businessName: r.business_name || '',
      rating: Math.max(1, Math.min(5, Math.round(Number(r.rating) || 0))),
      quote: String(r.quote),
      context: r.context || '',
      verifiedAs: r.verified_as || '',
      publishedAt: r.published_at ?? null,
    }));
  const first = rows[0] ?? {};
  const total = Number(first.total_published ?? reviews.length) || 0;
  const average = Number(first.average_rating ?? 0) || 0;
  return { reviews, total, average };
}

export interface AdminReviewRow {
  id: string;
  createdAt: string | null;
  name: string;
  email: string;
  context: string;
  rating: number;
  review: string;
  verifiedAs: string;
  consent: boolean;
  status: string;
}

export async function fetchAdminReviews(): Promise<AdminReviewRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('public_review_submissions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: any) => ({
    id: String(r.id),
    createdAt: r.created_at ?? null,
    name: r.full_name || r.display_name || '—',
    email: r.email || '',
    context: r.context || '',
    rating: Number(r.rating) || 0,
    review: r.review || r.review_text || r.quote || '',
    verifiedAs: r.verified_as || '',
    consent: r.consent_to_publish === true,
    status: String(r.status || 'pending'),
  }));
}

export async function publishReview(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.rpc('publish_public_review', { p_id: id });
  if (error) throw new Error(error.message);
}

export async function unpublishReview(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.rpc('unpublish_public_review', { p_id: id });
  if (error) throw new Error(error.message);
}
